// The quick-adds (inc/quick-add.php) of the Daily Schedule and the show page,
// which come with the page; spektrix-quick-add.js handles the cart (basket
// quantities, Add to Cart). The − / + buttons step the select, as in the
// Shortlist's Choose Date & Time (and on the Passes page's cards), Get
// Tickets opens reserved seating's pop-up, and How to book on PuSh Pass its
// pop-over.

// The forms with − / + steppers.
const STEPPED = ".quick-add, .pass-card-form";

// − at none and + at the most are disabled, as in the Shortlist. The cart's
// quantities arrive without a change event (spektrix-quick-add.js sets
// them), so this also runs when the pointer or focus reaches the form.
function syncSteps(scope: Element) {
  scope.querySelectorAll(".quantity-stepper").forEach((stepper) => {
    const select = stepper.querySelector("select");
    if (!select) return;
    stepper.querySelectorAll<HTMLButtonElement>(".quantity-step").forEach((button) => {
      const step = Number(button.dataset.step || 0);
      button.disabled = step < 0 ? select.selectedIndex <= 0 : select.selectedIndex >= select.options.length - 1;
    });
  });
}

function stepQuantity(button: HTMLButtonElement) {
  const select = button.closest(".quantity-stepper")?.querySelector("select");
  if (!select) return;
  const index = Math.min(
    select.options.length - 1,
    Math.max(0, select.selectedIndex + Number(button.dataset.step || 0)),
  );
  if (index === select.selectedIndex) return;
  select.selectedIndex = index;
  select.dispatchEvent(new Event("change", { bubbles: true }));
  // The button may have just disabled itself: keep focus in the stepper.
  if (button.disabled) select.focus();
}

// Reserved seating's Get Tickets (inc/quick-add.php) opens the pop-up beside
// it: a modal <dialog>, so it sits above everything, the Shortlist's own
// dialog included. While open it waits in the body (the Shortlist keeps its
// copies of the show page's quick-adds in a hidden slot, where a dialog
// can't show), and closing takes it home again. The Shortlist's own Get
// Tickets clicks this one.
const homes = new WeakMap<HTMLDialogElement, Comment>();

function openDialog(button: HTMLElement) {
  const dialog = button.parentElement?.querySelector<HTMLDialogElement>(":scope > .quick-add-dialog");
  if (!dialog || dialog.open) return;
  const home = document.createComment("quick-add-dialog");
  dialog.before(home);
  homes.set(dialog, home);
  document.body.append(dialog);
  dialog.showModal();
}

function closedDialog(dialog: HTMLDialogElement) {
  dialog.querySelectorAll(".success, .info, .error").forEach((message) => message.remove());
  const home = homes.get(dialog);
  homes.delete(dialog);
  if (home?.isConnected) home.replaceWith(dialog);
  else dialog.remove();
}

// How to book on PuSh Pass (inc/quick-add.php's push_pass_help()): the link
// opens a pop-over, in the top layer (the Popover API), so no column, table
// or pop-up clips it, and a click elsewhere or Escape closes it. Cached
// pages, and the Shortlist's copies of show pages, repeat the markup, so
// the ids that join the link (and Close) to the pop-over are given out
// here, on the first click.
let passHelps = 0;

function linkPassHelp(link: HTMLElement) {
  const tip = link.parentElement?.querySelector<HTMLElement>(":scope > .pass-help-tip");
  if (!tip || tip.id) return;
  tip.id = `pass-help-${++passHelps}`;
  link.setAttribute("popovertarget", tip.id);
  const close = tip.querySelector(".pass-help-close");
  close?.setAttribute("popovertarget", tip.id);
  close?.setAttribute("popovertargetaction", "hide");
}

// Over the link's top right corner, a little out from it, inside the
// window; it closes once the link scrolls out of view.
const openTips = new Set<HTMLElement>();

function placePassHelp(tip: HTMLElement) {
  const link = tip.parentElement?.querySelector(":scope > .pass-help-link");
  if (!link) return;
  const box = link.getBoundingClientRect();
  const view = document.documentElement;
  if (box.bottom < 0 || box.top > view.clientHeight) {
    tip.hidePopover();
    return;
  }
  const edge = 10;
  const left = Math.max(edge, Math.min(box.right + 8 - tip.offsetWidth, view.clientWidth - edge - tip.offsetWidth));
  const top = Math.max(edge, Math.min(box.top - 8, view.clientHeight - edge - tip.offsetHeight));
  Object.assign(tip.style, { inset: "auto", margin: "0", left: `${left}px`, top: `${top}px` });
}

function passHelpToggled(event: Event) {
  const tip = event.target;
  if (!(tip instanceof HTMLElement) || !tip.matches(".pass-help-tip")) return;
  const opening = (event as Event & { newState?: string }).newState === "open";
  // Hidden until placed: "toggle" comes once it's showing.
  if (event.type === "beforetoggle") {
    if (opening) tip.classList.add("is-placing");
    return;
  }
  if (opening) {
    openTips.add(tip);
    placePassHelp(tip);
    tip.classList.remove("is-placing");
  } else {
    openTips.delete(tip);
  }
}

function initQuickAdd() {
  document.addEventListener("click", (event) => {
    const target = event.target as Element | null;
    const button = target?.closest<HTMLButtonElement>(".quantity-stepper .quantity-step");
    if (button) stepQuantity(button);

    const opener = target?.closest<HTMLElement>("[data-quick-add-open]");
    if (opener) openDialog(opener);

    // Close, or a click on the backdrop (the dialog has no padding, so only
    // the backdrop is the dialog itself).
    const dialog = target?.closest<HTMLDialogElement>(".quick-add-dialog");
    if (dialog && (target === dialog || target?.closest("[data-quick-add-close]"))) dialog.close();
  });
  // Before the click's own work, which opens the pop-over its id names.
  document.addEventListener(
    "click",
    (event) => {
      const link = (event.target as Element | null)?.closest?.<HTMLElement>(".pass-help-link");
      if (link) linkPassHelp(link);
    },
    true,
  );
  // Neither toggle event bubbles.
  document.addEventListener("beforetoggle", passHelpToggled, true);
  document.addEventListener("toggle", passHelpToggled, true);
  const replace = () => openTips.forEach(placePassHelp);
  window.addEventListener("scroll", replace, { capture: true, passive: true });
  window.addEventListener("resize", replace);
  // "close" doesn't bubble.
  document.addEventListener(
    "close",
    (event) => {
      const dialog = event.target as Element;
      if (dialog instanceof HTMLDialogElement && dialog.matches(".quick-add-dialog")) closedDialog(dialog);
    },
    true,
  );
  ["change", "focusin", "pointerover"].forEach((type) => {
    document.addEventListener(type, (event) => {
      const form = (event.target as Element | null)?.closest?.(STEPPED);
      if (form) syncSteps(form);
    });
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initQuickAdd);
} else {
  initQuickAdd();
}

export {};
